import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, CheckCircle, Sparkle, Flag, Ticket, X } from "@phosphor-icons/react";

const CURRENCY_SYMBOL = { gbp: "£", usd: "$", eur: "€" };

export default function Subscribe() {
  const { user, refresh } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [params] = useSearchParams();
  const status = params.get("status");
  const sessionId = params.get("session_id");
  const [pollingMsg, setPollingMsg] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, percent_off, label}
  const [couponMsg, setCouponMsg] = useState("");
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/subscription/config");
        setCfg(data);
      } catch (err) {
        console.warn("Subscribe: failed to load /subscription/config", err);
      }
    })();
  }, []);

  // After Stripe redirect — poll the session to flip status to active server-side.
  useEffect(() => {
    if (status === "success" && sessionId) {
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/subscription/checkout/${sessionId}`);
          if (data.payment_status === "paid") {
            setPollingMsg("Payment confirmed — you're all set!");
            await refresh();
            return;
          }
        } catch (err) {
          console.warn("Subscribe: checkout poll failed (will retry)", err);
        }
        attempts += 1;
        if (attempts < 6) {
          setPollingMsg("Confirming payment...");
          setTimeout(poll, 2000);
        } else {
          setPollingMsg("Still confirming — refresh in a moment.");
        }
      };
      poll();
    }
  }, [status, sessionId, refresh]);

  const startCheckout = async () => {
    setError("");
    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const payload = { origin_url: origin };
      if (appliedCoupon) payload.coupon_code = appliedCoupon.code;
      const { data } = await api.post("/subscription/checkout", payload);
      if (data?.free) {
        // 100% off — already activated server-side. Refresh and stay on page.
        setPollingMsg("Free subscription activated — enjoy!");
        await refresh();
        setSubmitting(false);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  };

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setValidating(true);
    setCouponMsg("");
    try {
      const { data } = await api.post("/coupons/validate", { code });
      setAppliedCoupon(data);
      setCouponInput("");
      setCouponMsg(`✓ ${data.percent_off}% off applied`);
    } catch (err) {
      setAppliedCoupon(null);
      setCouponMsg(formatApiError(err) || "Code not valid");
    } finally {
      setValidating(false);
    }
  };

  const clearCoupon = () => {
    setAppliedCoupon(null);
    setCouponMsg("");
  };

  const sym = cfg ? (CURRENCY_SYMBOL[cfg.price_currency] || cfg.price_currency.toUpperCase() + " ") : "£";
  const isActive = user?.subscription_status === "active";
  const baseAmt = cfg ? Number(cfg.price_amount) : 0;
  const discountedAmt = appliedCoupon
    ? Math.round((baseAmt * (1 - appliedCoupon.percent_off / 100)) * 100) / 100
    : baseAmt;

  return (
    <div className="px-5 pt-6 pb-12">
      <Link to="/app" className="btn-ghost -ml-2" data-testid="subscribe-back">
        <ArrowLeft size={20} weight="bold" /> Back
      </Link>

      <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)] mt-4 mb-2">
        {isActive ? "You're on Pro" : "Go Pro"}
      </h1>
      <p className="text-[var(--text-secondary)] mb-6">
        {isActive
          ? "Thanks for supporting Fairway."
          : "Unlock the full experience — annual subscription, cancel anytime."}
      </p>

      {(() => {
        // Surface the Pro-gate message that the API client stashed in
        // sessionStorage when the user hit a locked feature.
        let proToast = null;
        try { proToast = sessionStorage.getItem("fairwayProToast"); } catch (_e) { /* sessionStorage may be disabled */ }
        if (proToast) {
          try { sessionStorage.removeItem("fairwayProToast"); } catch (_e) { /* sessionStorage may be disabled */ }
          return (
            <div
              className="rounded-2xl border-2 border-[var(--primary)] bg-[var(--accent-sand)] p-4 mb-5 flex items-start gap-3"
              data-testid="subscribe-pro-gate-banner"
            >
              <Sparkle size={22} weight="fill" className="text-[var(--primary)] shrink-0 mt-0.5" />
              <div>
                <div className="font-heading font-bold text-sm text-[var(--primary)] mb-0.5">Fairway Pro unlocks this</div>
                <div className="text-xs text-[var(--text)]">{proToast}</div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {status === "success" && pollingMsg && (
        <div
          className="rounded-2xl p-4 mb-5 flex items-center gap-3"
          style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
          data-testid="subscribe-success-banner"
        >
          <CheckCircle size={24} weight="fill" />
          <div className="font-heading font-bold text-sm">{pollingMsg}</div>
        </div>
      )}
      {status === "cancelled" && (
        <div className="rounded-2xl border border-[var(--border-default)] p-4 mb-5 text-sm" data-testid="subscribe-cancelled-banner">
          Payment cancelled — no card was charged.
        </div>
      )}

      {isActive ? (
        <div className="surface-card p-6 text-center" data-testid="already-subscribed">
          <div
            className="w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center"
            style={{ background: "var(--accent-terracotta)", color: "#F2EEE7" }}
          >
            <Sparkle size={28} weight="fill" />
          </div>
          <h2 className="font-heading font-bold text-xl text-[var(--primary)] mb-1">Fairway Pro</h2>
          {user?.subscription_current_period_end && (
            <p className="text-sm text-[var(--text-secondary)]">
              Renews / expires {new Date(user.subscription_current_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          )}
        </div>
      ) : (
        cfg && (
          <div className="surface-card p-6 mb-4" data-testid="subscribe-card">
            <div className="flex items-center gap-2 mb-2">
              <Flag size={16} weight="fill" className="text-[var(--accent-terracotta)]" />
              <div className="text-[10px] uppercase tracking-[3px] font-bold text-[var(--text-secondary)]">{cfg.plan_name}</div>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              {appliedCoupon ? (
                <>
                  <span
                    className="font-mono-num font-black text-5xl text-[var(--primary)]"
                    data-testid="subscribe-price-discounted"
                  >
                    {sym}{discountedAmt.toFixed(2)}
                  </span>
                  <span
                    className="font-mono-num text-base text-[var(--text-secondary)] line-through"
                    data-testid="subscribe-price-original"
                  >
                    {sym}{baseAmt.toFixed(2)}
                  </span>
                  <span className="text-sm text-[var(--text-secondary)] font-heading">/year</span>
                </>
              ) : (
                <>
                  <span className="font-mono-num font-black text-5xl text-[var(--primary)]" data-testid="subscribe-price">
                    {sym}{baseAmt.toFixed(2)}
                  </span>
                  <span className="text-sm text-[var(--text-secondary)] font-heading">/year</span>
                </>
              )}
            </div>
            {appliedCoupon && (
              <div
                className="flex items-center justify-between gap-2 rounded-xl px-3 py-2 mb-3 text-xs font-bold"
                style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
                data-testid="subscribe-coupon-applied"
              >
                <span className="flex items-center gap-1.5">
                  <Ticket size={14} weight="fill" />
                  Code{" "}
                  <span className="font-mono-num tracking-wider">{appliedCoupon.code}</span>
                  {" "}· {appliedCoupon.percent_off}% off
                </span>
                <button
                  type="button"
                  onClick={clearCoupon}
                  data-testid="subscribe-coupon-clear"
                  className="opacity-70"
                  aria-label="Remove code"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            )}
            {cfg.trial_days > 0 && (
              <div className="font-heading font-bold text-sm text-[var(--accent-terracotta)] mb-3" data-testid="subscribe-trial-banner">
                {cfg.trial_days}-day free trial · cancel anytime
              </div>
            )}
            <ul className="space-y-2 mt-4 mb-5 text-sm text-[var(--primary)]">
              {[
                "Unlimited rounds (free is capped at 2)",
                "Every tee colour (free is yellow only)",
                "Leagues — create, join, money pot",
                "Smartwatch + offline rounds",
                "Your club bag yardages saved",
                "Unlimited favourite courses (free caps at 5)",
                "Branded shareable round recaps (fourball)",
                "Course scorecard scanner (AI vision)",
              ].map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-[var(--accent-terracotta)] shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            {!appliedCoupon && (
              <div className="mb-3" data-testid="subscribe-coupon-row">
                <details>
                  <summary
                    className="cursor-pointer text-xs font-bold font-heading text-[var(--text-secondary)] flex items-center gap-1.5 select-none"
                    data-testid="subscribe-coupon-toggle"
                  >
                    <Ticket size={14} weight="bold" />
                    Got a code?
                  </summary>
                  <div className="flex gap-2 mt-2">
                    <input
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                      placeholder="Enter code"
                      maxLength={32}
                      data-testid="subscribe-coupon-input"
                      className="input-field flex-1 uppercase tracking-wider font-mono-num"
                      style={{ letterSpacing: "0.1em" }}
                    />
                    <button
                      type="button"
                      onClick={applyCoupon}
                      disabled={validating || !couponInput.trim()}
                      data-testid="subscribe-coupon-apply"
                      className="text-xs font-heading font-bold px-3 rounded-xl"
                      style={{ background: "var(--primary)", color: "#F2EEE7" }}
                    >
                      {validating ? "…" : "Apply"}
                    </button>
                  </div>
                  {couponMsg && (
                    <p
                      className={`text-xs mt-1.5 font-bold ${couponMsg.startsWith("✓") ? "text-[var(--status-good)]" : "text-[var(--status-bad)]"}`}
                      data-testid="subscribe-coupon-msg"
                    >
                      {couponMsg}
                    </p>
                  )}
                </details>
              </div>
            )}

            {error && (
              <div className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-3">
                {error}
              </div>
            )}
            <button
              onClick={startCheckout}
              disabled={submitting || !cfg.active}
              className="btn-primary w-full"
              data-testid="subscribe-cta"
            >
              {submitting
                ? "Loading..."
                : appliedCoupon && appliedCoupon.percent_off === 100
                ? "Activate free subscription"
                : cfg.trial_days > 0
                ? `Start ${cfg.trial_days}-day free trial`
                : "Subscribe"}
            </button>
            {!cfg.active && (
              <p className="text-xs text-[var(--text-secondary)] mt-3 text-center">Subscriptions are paused — check back soon.</p>
            )}
          </div>
        )
      )}
    </div>
  );
}
